import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { captureAuthFailure, addBreadcrumb, captureException } from "@/lib/sentry";
import { z } from "zod";
import { ApiContractError } from "@/lib/apiFetch";

export class RateLimitError extends Error {
  readonly status = 429;
  readonly retryAfterSeconds: number | null;
  constructor(retryAfterSeconds: number | null) {
    const wait = retryAfterSeconds ? ` Please wait ${retryAfterSeconds} second${retryAfterSeconds !== 1 ? "s" : ""} before trying again.` : " Please wait a moment before trying again.";
    super(`You're submitting too quickly.${wait}`);
    this.name = "RateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export class TokenExpiredError extends Error {
  readonly status = 401;
  constructor(message = "Session expired") {
    super(message);
    this.name = "TokenExpiredError";
  }
}

export class ApiHttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiHttpError";
  }
}

export async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    if (res.status === 429) {
      const retryAfter = res.headers.get("retry-after");
      const seconds = retryAfter ? parseInt(retryAfter, 10) : null;
      throw new RateLimitError(Number.isFinite(seconds) ? seconds : null);
    }
    const text = (await res.text()) || res.statusText;
    let requestId: string | undefined = res.headers.get("x-request-id") ?? undefined;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object" && typeof parsed.requestId === "string") {
        requestId = parsed.requestId;
      }
    } catch {
      // non-JSON body — keep header-derived requestId
    }
    throw new ApiHttpError(res.status, `${res.status}: ${text}`, requestId);
  }
}

// ---------------------------------------------------------------------------
// Consecutive network-failure tracker
// Counts errors that look like connectivity/server problems (not expected 4xx).
// After FAILURE_THRESHOLD consecutive failures, dispatches "api-unreachable".
// Resets and dispatches "api-reachable" when a call succeeds.
// ---------------------------------------------------------------------------
const FAILURE_THRESHOLD = 3;
let _consecutiveFailures = 0;

function isAppError(err: unknown): boolean {
  // RateLimitError is an expected app-level response, not a connectivity issue
  if (err instanceof RateLimitError) return true;
  // Any HTTP 4xx error means the server responded — the backend is reachable.
  // Only network-level failures (TypeError, ECONNREFUSED) and 5xx errors
  // indicate genuine connectivity/server problems.
  const msg = err instanceof Error ? err.message : String(err);
  return /^4\d{2}:/.test(msg);
}

function recordFetchFailure(err: unknown): void {
  if (isAppError(err)) return;
  _consecutiveFailures += 1;
  if (_consecutiveFailures === FAILURE_THRESHOLD) {
    window.dispatchEvent(new CustomEvent("api-unreachable"));
  }
}

function recordFetchSuccess(): void {
  if (_consecutiveFailures >= FAILURE_THRESHOLD) {
    window.dispatchEvent(new CustomEvent("api-reachable"));
  }
  _consecutiveFailures = 0;
}
// ---------------------------------------------------------------------------

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  try {
    const res = await fetch(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    await throwIfResNotOk(res);
    recordFetchSuccess();
    return res;
  } catch (err) {
    recordFetchFailure(err);
    throw err;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    try {
      const res = await fetch(queryKey.join("/") as string, {
        credentials: "include",
      });

      if (res.status === 401) {
        captureAuthFailure(queryKey.join("/") as string);
        if (unauthorizedBehavior === "returnNull") {
          recordFetchSuccess();
          return null;
        }
      }

      await throwIfResNotOk(res);
      recordFetchSuccess();
      return await res.json();
    } catch (err) {
      recordFetchFailure(err);
      throw err;
    }
  };

// ---------------------------------------------------------------------------
// zParse — lightweight schema validation helper
//
// Use this when you have a raw fetch() result that you want to validate
// against a Zod schema without the full apiFetch/contract machinery.
// On success, returns typed data. On failure, emits a Sentry breadcrumb +
// captureException and throws an ApiContractError so the caller gets a
// descriptive typed error instead of a silent runtime undefined.
//
// Usage inside a queryFn:
//   const raw = await fetch("/api/admin/all-bookings").then(r => r.json());
//   return zParse("adminAllBookings", mySchema, raw);
//
// Migration pattern for existing `fetch().then(r => r.json())` queryFn calls
// that lack any schema validation (Task #1001):
//   1. If a typed `ApiContract` exists in shared/contracts.ts for the endpoint,
//      prefer migrating to `apiFetch(contract)` — that gives full param/body
//      typing plus response validation in one call.
//   2. Otherwise, wrap the parsed JSON in `zParse("<endpointName>", schema,
//      raw)` with a lightweight inline Zod schema (e.g. `z.array(z.unknown())`
//      for list endpoints or `z.object({ key: ... }).passthrough()` for object
//      endpoints with a known shape). Cast the result back to the existing
//      TypeScript type if the inline schema is intentionally loose.
//   3. Schema validation failures emit a Sentry breadcrumb +
//      `captureException` with the endpoint name so unexpected server-shape
//      changes surface as a typed error rather than a silent UI bug.
// ---------------------------------------------------------------------------
export function zParse<T extends z.ZodTypeAny>(
  endpointName: string,
  schema: T,
  data: unknown,
): z.infer<T> {
  const result = schema.safeParse(data);
  if (!result.success) {
    const issues = result.error.issues;
    addBreadcrumb(
      `[zParse] Response for "${endpointName}" failed schema validation`,
      {
        endpoint: endpointName,
        issues: issues
          .slice(0, 5)
          .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`),
      },
    );
    const err = new ApiContractError(endpointName, issues);
    captureException(err, { endpoint: endpointName });
    throw err;
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// staleTime policy
//
// The global default is Infinity (data never considered stale) + no window-focus
// refetch. This is intentional for the vast majority of queries: studio config,
// artist profiles, flash designs, and other content that changes only through
// explicit admin mutations (which also invalidate the relevant cache keys).
//
// The following query categories MUST override staleTime and enable
// refetchOnWindowFocus explicitly because their data can change independently
// of client-initiated mutations and stale reads directly cause broken UX:
//
//   (a) Availability / slots — `/api/availability`, `/api/availability/:slug`
//       An artist can be marked booked-out at any time. A client viewing stale
//       "available" data who submits a booking gets a confusing server rejection.
//       Override: staleTime: 3 * 60 * 1000, refetchOnWindowFocus: true
//
//   (b) Booked times — `booked-times` key (BookingCalendar)
//       A slot can be taken by another client between page-load and submission.
//       The server re-checks at submission time (idempotency guard), but the UI
//       should show an up-to-date calendar to avoid confusing "slot taken" errors.
//       Override: staleTime: 30_000, refetchOnWindowFocus: true (already 30 s)
//
//   (c) Live booking status / admin inbox counts
//       These reflect server state that changes without a client mutation.
//       Override: staleTime: <appropriate short window>, refetchOnWindowFocus: true
//
// For all other queries, Infinity + no focus refetch is correct. Admin pages use
// explicit query invalidation (queryClient.invalidateQueries) after mutations.
// ---------------------------------------------------------------------------
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;
        const msg = error instanceof Error ? error.message : String(error);
        if (/^(401|403|404|422):/.test(msg)) return false;
        return true;
      },
      retryDelay: 800,
    },
    mutations: {
      retry: false,
    },
  },
});
